import React, { useEffect, useState } from "react";
import { Newspaper, X, ExternalLink } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const fmt = (iso) => {
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return "";
  }
};

const InternalNewsList = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null); // article for modal

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const res = await fetch(`${BACKEND}/api/news`);
        if (res.ok && alive) setArticles(await res.json());
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    return () => {
      alive = false;
    };
  }, []);

  if (loading) return null;
  if (articles.length === 0) return null;

  return (
    <section data-testid="internal-news" className="mb-8">
      <div className="mb-4 flex items-center gap-2">
        <Newspaper className="w-4 h-4 text-gold" />
        <div className="font-condensed uppercase tracking-[0.35em] text-[11px] text-gold">
          From the Newsdesk
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {articles.map((a) => (
          <article
            key={a.id}
            data-testid={`news-article-${a.id}`}
            className="group flex flex-col bg-jet-surface border border-gold/25 hover:border-gold/60 rounded-lg overflow-hidden transition-colors"
          >
            {a.image_url && (
              <div className="aspect-[16/9] bg-black overflow-hidden">
                <img
                  src={a.image_url}
                  alt={a.title}
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
              </div>
            )}
            <div className="p-5 flex-1 flex flex-col">
              {a.category && (
                <div className="font-condensed uppercase tracking-[0.3em] text-[10px] text-gold mb-2">
                  {a.category}
                </div>
              )}
              <h3 className="font-display text-xl md:text-2xl text-white uppercase leading-tight">
                {a.title}
              </h3>
              <div className="font-condensed uppercase tracking-widest text-[10px] text-neutral-500 mt-1">
                {fmt(a.created_at)}
              </div>
              <p className="mt-3 font-body text-sm text-neutral-300 leading-relaxed line-clamp-4">
                {a.body}
              </p>
              <div className="mt-4 flex items-center gap-3">
                <button
                  data-testid={`news-read-${a.id}`}
                  onClick={() => setOpen(a)}
                  className="inline-flex items-center gap-2 border border-gold/50 hover:border-gold text-gold font-condensed uppercase tracking-widest text-[10px] px-3 py-2 rounded-full transition-colors"
                >
                  Read More
                </button>
                {a.external_url && (
                  <a
                    href={a.external_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`news-external-${a.id}`}
                    className="inline-flex items-center gap-1.5 text-gold/80 hover:text-gold font-condensed uppercase tracking-widest text-[10px]"
                  >
                    <ExternalLink className="w-3 h-3" />
                    Source
                  </a>
                )}
              </div>
            </div>
          </article>
        ))}
      </div>

      {/* Modal */}
      {open && (
        <div
          data-testid="news-modal"
          className="fixed inset-0 z-[70] bg-black/85 backdrop-blur-sm px-4 py-8 overflow-y-auto"
          onClick={() => setOpen(null)}
        >
          <div
            className="max-w-3xl mx-auto bg-jet-surface border border-gold/40 rounded-lg overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {open.image_url && (
              <div className="aspect-[16/9] bg-black overflow-hidden">
                <img
                  src={open.image_url}
                  alt={open.title}
                  className="w-full h-full object-cover"
                />
              </div>
            )}
            <div className="p-6 md:p-8 relative">
              <button
                data-testid="news-modal-close"
                onClick={() => setOpen(null)}
                aria-label="Close"
                className="absolute top-4 right-4 w-9 h-9 grid place-items-center rounded-full bg-black/70 hover:bg-crimson text-white transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
              {open.category && (
                <div className="font-condensed uppercase tracking-[0.3em] text-[10px] text-gold mb-2">
                  {open.category}
                </div>
              )}
              <h2 className="font-display text-3xl md:text-4xl text-white uppercase leading-tight pr-10">
                {open.title}
              </h2>
              <div className="font-condensed uppercase tracking-widest text-[10px] text-neutral-500 mt-2">
                {fmt(open.created_at)}
              </div>
              <div className="mt-5 font-body text-[15px] text-neutral-200 leading-relaxed whitespace-pre-wrap">
                {open.body}
              </div>
              {open.external_url && (
                <a
                  href={open.external_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-6 inline-flex items-center gap-2 border border-gold/50 hover:border-gold text-gold font-condensed uppercase tracking-widest text-[10px] px-4 py-2 rounded-full transition-colors"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                  Original Source
                </a>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default InternalNewsList;
